import { useState, useEffect } from "react";
import { Avatar, Col, Divider, Drawer, Modal, Row, Space, Input } from "antd";
import { Button } from "@shared/ui/Button";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useGetAgencies } from "@entities/agency/lib/hooks/useGetAgencies";
import { useLinkInfluencer } from "@entities/agency/lib/hooks/useLinkInfluencer";
import { useDeLinkInfluencer } from "@entities/agency/lib/hooks/useDeLinkInfluencer";
import { ToastService } from "@shared/lib/services";
import styles from "../styles.module.scss";
import { Agency } from "@shared/lib/types/agency";
import { useGetInfluencer } from "@entities/influencer/lib/hooks/useGetInfluencerById";
import { AgencySelectForm } from "./AgencySelectForm";
import { AgencyDetails } from "@entities/agency/ui/AgencyDetails";

interface Props {
  linkedAgencies: { _id: string; name: string; profilePicUrl: string }[];
  influencerId: string;
}

export function LinkedAgencyList({ linkedAgencies, influencerId }: Props) {
  const { isDeLinking, isDeLinked, deLinkInfluencer, variables } =
    useDeLinkInfluencer();
  const { isLinked, isLinking, linkInfluencer } = useLinkInfluencer();

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [agencyId, setAgencyId] = useState<string>("");

  const handleDelete = (agencyId: string) => {
    deLinkInfluencer({ agencyId, influencerId });
  };

  const handleAdd = (agencyId: string) => {
    linkInfluencer({ agencyId: agencyId, influencerId });
  };

  useEffect(() => {
    if (isLinked) {
      ToastService.success("Agency Linked");
      setModalVisible(false);
    }
  }, [isLinked]);

  useEffect(() => {
    if (isDeLinked) {
      ToastService.success("Agency Delinked");
    }
  }, [isDeLinked]);

  return (
    <div className={styles.card}>
      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          alignItems: "start",
          marginBottom: "12px",
        }}
      >
        <div className={styles.permTitle}>Linked Agencies</div>
        <Button
          variant="outlined"
          size="middle"
          onClick={() => setModalVisible(true)}
          icon={<PlusOutlined />}
          iconPosition="end"
        >
          Link Agency
        </Button>
      </Space>
      <Divider style={{ margin: 0, marginBottom: 16 }} />
      {linkedAgencies.length ? (
        <div className={styles.permContent}>
          {linkedAgencies.map((agency) => (
            <>
              <AgencyTile
                agency={agency}
                handleDelete={handleDelete}
                isLoading={isDeLinking}
                selectedId={variables?.agencyId || ""}
                handleSelect={(agencyId) => setAgencyId(agencyId)}
              />
              <Divider style={{ margin: 0 }} />
            </>
          ))}
        </div>
      ) : (
        <div>No Agencies linked</div>
      )}
      <Modal
        title="Link Agency"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
        width={800}
      >
        <AgencySelectForm
          handleAdd={handleAdd}
          onClose={() => setModalVisible(false)}
          isLoading={isLinking}
        />
      </Modal>
      <Drawer
        destroyOnClose
        title={"Agency"}
        open={Boolean(agencyId)}
        onClose={() => {
          setAgencyId("");
        }}
        footer={null}
        width={800}
      >
        {agencyId && <AgencyDetails agencyId={agencyId} />}
      </Drawer>
    </div>
  );
}

function AgencyTile({
  agency,
  handleDelete,
  isLoading,
  selectedId,
  handleSelect,
}: {
  agency: { _id: string; name: string; profilePicUrl: string };
  handleDelete: (agencyId: string) => void;
  isLoading: boolean;
  selectedId: string;
  handleSelect: (agencyId: string) => void;
}) {
  return (
    <Row style={{ width: "100%" }} justify={"space-between"}>
      <Col span={14} onClick={() => handleSelect(agency._id)}>
        <Space style={{ gap: "8px", alignItems: "center" }}>
          <Avatar src={agency.profilePicUrl} size={"large"} />
          <div className={styles.permItemDesc}>
            <span
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {agency.name}
            </span>
          </div>
        </Space>
      </Col>
      <Col
        span={4}
        style={{
          justifyContent: "flex-end",
          display: "flex",
          height: "max-content",
        }}
      >
        <Button
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDelete(agency._id);
          }}
          loading={selectedId === agency._id && isLoading}
        ></Button>
      </Col>
    </Row>
  );
}
